import { isDeepEqual } from '@reatom/framework'
import React from 'react'

import { applyWidgetDropTarget, widgetPairSide } from '../../model'

import type { WidgetDropTarget, WidgetRows } from '../../model'

/**
 * Share of the row width a drag has to travel sideways before it counts as
 * pairing intent, so that dragging straight up or down never pairs by accident.
 */
const PAIR_INTENT_RATIO = 0.1

export type WidgetDragInfo = {
  /** Vertical pointer position in viewport coordinates. */
  y: number
  /** Horizontal travel since the drag started. */
  offsetX: number
}

type Params<T extends string> = {
  rows: WidgetRows<T>
  /** Whether the first row may hold two widgets side by side. */
  pairing?: boolean
  onChange: (rows: WidgetRows<T>) => void
}

type RowBand = { top: number; bottom: number }

/** Vertical bands of every row, relative to the top of the list. */
const measureRowBands = (group: HTMLElement) => {
  const groupTop = group.getBoundingClientRect().top

  return [...group.children]
    .reduce<Array<RowBand>>((bands, child) => {
      const rect = child.getBoundingClientRect()
      const top = rect.top - groupTop
      const row = bands.find((band) => Math.abs(band.top - top) < 1)

      if (row) {
        row.bottom = Math.max(row.bottom, top + rect.height)
      } else {
        bands.push({ top, bottom: top + rect.height })
      }
      return bands
    }, [])
    .sort((left, right) => left.top - right.top)
}

const resolveRowIndex = (bands: Array<RowBand>, y: number) => {
  const index = bands.findIndex((band) => y < band.bottom)
  return index === -1 ? bands.length : index
}

export const useWidgetRowsDrag = <T extends string>({
  rows,
  pairing = false,
  onChange,
}: Params<T>) => {
  const groupRef = React.useRef<HTMLDivElement>(null)
  const rowBandsRef = React.useRef<Array<RowBand>>([])
  const rowsRef = React.useRef(rows)
  rowsRef.current = rows
  const [dragging, setDragging] = React.useState(false)

  /**
   * Rows are measured once per gesture so that the drop zones stay where the
   * widgets were when the drag began, instead of moving as the list reflows.
   * Called at pointerdown, before motion's pan threshold, so the first move
   * already has bands. Does not toggle `dragging` — that waits for a real pan.
   */
  const handleDragPrepare = () => {
    const group = groupRef.current
    rowBandsRef.current = group ? measureRowBands(group) : []
  }

  const handleDragStart = () => {
    setDragging(true)
  }

  const handleDragEnd = () => {
    setDragging(false)
  }

  const resolveTarget = (
    widget: T,
    info: WidgetDragInfo,
    rect: DOMRect,
  ): WidgetDropTarget => {
    const index = resolveRowIndex(rowBandsRef.current, info.y - rect.top)

    if (!pairing || index !== 0) {
      return { kind: 'row', index }
    }

    const intent = rect.width * PAIR_INTENT_RATIO
    if (info.offsetX <= -intent) {
      return { kind: 'pair', side: 'start' }
    }
    if (info.offsetX >= intent) {
      return { kind: 'pair', side: 'end' }
    }

    // Without sideways intent the widget keeps the width it already has.
    const side = widgetPairSide(rowsRef.current, widget)
    return side ? { kind: 'pair', side } : { kind: 'row', index }
  }

  const handleDrag = (widget: T, info: WidgetDragInfo) => {
    const group = groupRef.current
    if (!group) {
      return
    }

    const target = resolveTarget(widget, info, group.getBoundingClientRect())
    const next = applyWidgetDropTarget(rowsRef.current, widget, target)

    if (!isDeepEqual(rowsRef.current, next)) {
      onChange(next)
    }
  }

  const itemProps = (widget: T) => ({
    // Layout transforms on siblings would compete for paint order; keep them
    // off for the whole gesture so the drag transform alone stacks on top.
    layout: !dragging,
    onDragPrepare: handleDragPrepare,
    onDragStart: handleDragStart,
    onDragMove: (info: WidgetDragInfo) => handleDrag(widget, info),
    onDragEnd: handleDragEnd,
  })

  return { groupRef, dragging, itemProps }
}
